/**
 * Wind Chime 1.0.0 — dependency-free, synthesized wind chimes.
 * This is the source module. Run `npm run build` to regenerate wind-chime.js.
 */
const DEFAULTS = Object.freeze({
  volume: 0.35,
  muted: false,
  baseFrequency: 523.251,
  scale: Object.freeze([0, 2, 4, 7, 9, 12, 14, 16]),
  decay: 6,
  brightness: 0.55,
  detune: 5,
  reverb: 0.22,
  stereoWidth: 0.8,
  wind: 0.4,
  variability: 0.7,
  gustiness: 0.55,
  density: 1.8,
  maximumSilence: 4,
  maxVoices: 12,
  pauseWhenHidden: true,
});

const RANGES = {
  volume: [0, 1], baseFrequency: [80, 2000], decay: [0.4, 20],
  brightness: [0, 1], detune: [0, 30], reverb: [0, 1],
  stereoWidth: [0, 1], wind: [0, 1], variability: [0, 1],
  gustiness: [0, 1], density: [0, 6], maximumSilence: [0.1, 60], maxVoices: [1, 32],
};
const EVENTS = new Set(['strike', 'wind', 'statechange', 'error']);
const STEP = 0.05;
const LOOKAHEAD = 0.12;
const FLOOR = 0.00001;
const clamp = (x, min = 0, max = 1) => Math.min(max, Math.max(min, x));

function object(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object.`);
  }
}

function number(value, min, max, name) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${name} must be a finite number between ${min} and ${max}.`);
  }
  return value;
}

function validate(patch, previous) {
  object(patch, 'Options');
  const next = { ...previous };
  for (const [key, value] of Object.entries(patch)) {
    if (!Object.hasOwn(DEFAULTS, key)) throw new TypeError(`Unknown option: ${key}`);
    if (key === 'scale') {
      if (!Array.isArray(value) || value.length < 1 || value.length > 32) {
        throw new RangeError('scale must contain 1–32 semitone offsets.');
      }
      next.scale = Array.from(value, (note) => number(note, -24, 36, 'scale offset'));
    } else if (key === 'muted' || key === 'pauseWhenHidden') {
      if (typeof value !== 'boolean') throw new TypeError(`${key} must be a boolean.`);
      next[key] = value;
    } else {
      number(value, ...RANGES[key], key);
      if (key === 'maxVoices' && !Number.isInteger(value)) {
        throw new RangeError('maxVoices must be an integer.');
      }
      next[key] = value;
    }
  }
  if (next.scale.some((note) => next.baseFrequency * 2 ** (note / 12) > 10000)) {
    throw new RangeError('The scale and baseFrequency must keep every note at or below 10000 Hz.');
  }
  return next;
}

function seededRandom(seed) {
  let value = seed >>> 0;
  return () => {
    value = (value + 0x6D2B79F5) >>> 0;
    let t = Math.imul(value ^ (value >>> 15), 1 | value);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function smooth(param, value, time, seconds = 0.04) {
  if (seconds === 0) {
    param.cancelScheduledValues(time);
    param.setValueAtTime(value, time);
    return;
  }
  param.setTargetAtTime(value, time, seconds / 3);
}

function disconnect(node) {
  try { node.disconnect(); } catch { /* A node may already be disconnected. */ }
}

class WindChime {
  /** Construction is silent and safe during server-side rendering. */
  constructor(options = {}) {
    object(options, 'Options');
    const { audioContext = null, destination = null, seed, ...settings } = options;
    this._options = validate(settings, DEFAULTS);
    if (seed !== undefined) {
      number(seed, 0, 4294967295, 'seed');
      if (!Number.isInteger(seed)) throw new RangeError('seed must be an integer.');
    }
    if (audioContext !== null && (typeof audioContext.resume !== 'function' ||
        typeof audioContext.createOscillator !== 'function' ||
        typeof audioContext.startRendering === 'function')) {
      throw new TypeError('audioContext must be a live AudioContext, not an OfflineAudioContext.');
    }
    if (destination !== null && (!audioContext || destination.context !== audioContext)) {
      throw new TypeError('destination must be an AudioNode belonging to the supplied audioContext.');
    }
    this._context = audioContext;
    this._destination = destination;
    this._ownsContext = !audioContext;
    this._random = seed === undefined ? Math.random : seededRandom(seed);
    this._state = 'idle';
    this._listeners = new Map();
    this._voices = new Set();
    this._graph = null;
    this._timer = null;
    this._startPromise = null;
    this._destroyPromise = null;
    this._generation = 0;
    this._wantRunning = false;
    this._starting = false;
    this._destroyed = false;
    this._document = null;
    this._lastNote = -1;
    this._lastStrikeAt = 0;
    this._windLevel = 0;
    this._baseWind = this._options.wind;
    this._targetWind = this._options.wind;
    this._targetUntil = 0;
    this._gust = null;
    this._windEventAt = 0;
    this._onVisibility = () => this._syncPlayback();
    this._onContextState = () => {
      if (this._context.state === 'closed' && !this._destroyed) {
        this._wantRunning = false;
        this._halt(0);
        this._setState('stopped', 'context-closed');
        this._emit('error', new Error('The AudioContext was closed. Destroy this instance and create a new one.'));
      } else this._syncPlayback();
    };
  }

  static get defaults() { return Object.freeze({ ...DEFAULTS, scale: Object.freeze([...DEFAULTS.scale]) }); }
  static get isSupported() { return Boolean(globalThis.AudioContext || globalThis.webkitAudioContext); }
  get state() { return this._state; }
  get isRunning() { return this._state === 'running'; }
  get audioContext() { return this._context; }
  get activeVoices() { return this._voices.size; }
  get windLevel() { return this._windLevel; }
  get frequencies() { return this._options.scale.map((n) => this._options.baseFrequency * 2 ** (n / 12)); }
  get options() { return Object.freeze({ ...this._options, scale: Object.freeze([...this._options.scale]) }); }
  get volume() { return this._options.volume; }
  set volume(value) { this.configure({ volume: value }); }
  get muted() { return this._options.muted; }
  set muted(value) { this.configure({ muted: value }); }
  get wind() { return this._options.wind; }
  set wind(value) { this.configure({ wind: value }); }

  /** Call directly in a click/tap handler; repeated concurrent calls share a promise. */
  start() {
    this._assertAlive();
    if (this._startPromise) return this._startPromise;
    if (this.isRunning && this._wantRunning && this._context?.state === 'running') return Promise.resolve(this);
    const generation = ++this._generation;
    this._wantRunning = true;
    this._starting = true;
    this._setState('starting', 'start');
    if (generation !== this._generation || this._destroyed) return Promise.resolve(this);
    let resumed;
    try {
      this._initialize();
      if (this._context.state === 'closed') throw new Error('The AudioContext is closed. Create a new WindChime instance.');
      // No asynchronous work before resume(): preserve the browser's user gesture.
      resumed = this._context.state === 'running' ? Promise.resolve() : this._context.resume();
    } catch (error) { resumed = Promise.reject(error); }
    const pending = Promise.resolve(resumed).then(() => {
      if (generation !== this._generation || this._destroyed) return this;
      if (this._context.state !== 'running') {
        throw new Error('Audio is not running. Call start() again directly from a user click or tap.');
      }
      this._starting = false;
      this._baseWind = this._options.wind;
      this._targetWind = this._options.wind;
      this._targetUntil = 0;
      this._gust = null;
      this._syncPlayback();
      return this;
    }).catch((error) => {
      if (generation !== this._generation || this._destroyed) return this;
      this._starting = false;
      this._wantRunning = false;
      this._halt(0.05);
      this._setState('stopped', 'start-failed');
      this._emit('error', error);
      throw error;
    }).finally(() => {
      if (this._startPromise === pending) this._startPromise = null;
    });
    this._startPromise = pending;
    return pending;
  }

  /** Stop scheduling and fade all output, including reverb. The instance is reusable. */
  stop({ fadeOut = 0.3 } = {}) {
    this._assertAlive();
    number(fadeOut, 0, 5, 'fadeOut');
    ++this._generation;
    this._startPromise = null;
    this._starting = false;
    this._wantRunning = false;
    this._gust = null;
    this._halt(fadeOut);
    this._setState('stopped', 'stop');
    return this;
  }

  /** Stop scheduling immediately and let active chimes finish naturally. */
  stopGracefully() {
    this._assertAlive();
    ++this._generation;
    this._startPromise = null;
    this._starting = false;
    this._wantRunning = false;
    this._gust = null;
    if (this._timer !== null) globalThis.clearInterval(this._timer);
    this._timer = null;
    this._windLevel = 0;
    this._setState(this._voices.size === 0 ? 'stopped' : 'stopping', 'graceful-stop');
    return this;
  }

  /** Update sound and wind controls atomically. Constructor-only options are rejected. */
  configure(patch) {
    this._assertAlive();
    const next = validate(patch, this._options);
    if (this._context && this._graph) this._validateSampleRate(next);
    this._options = next;
    if (Object.hasOwn(patch, 'wind') || Object.hasOwn(patch, 'variability')) {
      this._targetUntil = 0;
      if (next.wind === 0) {
        this._baseWind = this._targetWind = 0;
        if (this._gust?.source === 'natural') this._gust = null;
      }
    }
    if (this._graph) this._applyMix();
    this._syncPlayback();
    return this;
  }

  /** Add one temporary gust; a new call replaces the current gust. */
  gust(strength = 0.65, duration = 4) {
    this._assertRunning();
    number(strength, 0, 1, 'strength');
    number(duration, 0.2, 30, 'duration');
    this._gust = { start: this._context.currentTime, strength, duration, source: 'manual' };
    return this;
  }

  /** Strike one tube. Returns scheduled strike metadata, or null if suppressed. */
  strike(index = null, options = {}) {
    this._assertRunning();
    object(options, 'Strike options');
    for (const key of Object.keys(options)) {
      if (key !== 'velocity' && key !== 'pan') throw new TypeError(`Unknown strike option: ${key}`);
    }
    if (index !== null && (!Number.isInteger(index) || index < 0 || index >= this._options.scale.length)) {
      throw new RangeError('index must be null or a valid zero-based scale index.');
    }
    const { velocity = 0.65, pan } = options;
    number(velocity, 0, 1, 'velocity');
    if (pan !== undefined) number(pan, -1, 1, 'pan');
    return this._playStrike(this._context.currentTime + 0.005, index, velocity, pan, 'manual');
  }

  on(event, listener) {
    this._assertAlive();
    if (!EVENTS.has(event)) throw new TypeError(`Unknown event: ${event}`);
    if (typeof listener !== 'function') throw new TypeError('listener must be a function.');
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(listener);
    return () => this.off(event, listener);
  }

  off(event, listener) {
    this._listeners.get(event)?.delete(listener);
    return this;
  }

  /** Permanently dispose. Never closes or suspends a caller-owned context. */
  destroy() {
    if (this._destroyPromise) return this._destroyPromise;
    if (this._destroyed) return Promise.resolve();
    this.stop({ fadeOut: 0 });
    this._destroyed = true;
    this._context?.removeEventListener('statechange', this._onContextState);
    this._document?.removeEventListener('visibilitychange', this._onVisibility);
    for (const voice of [...this._voices]) this._cleanupVoice(voice);
    if (this._graph) {
      for (const node of this._graph.nodes) disconnect(node);
      this._graph = null;
    }
    this._setState('destroyed', 'destroy');
    this._listeners.clear();
    const closing = this._ownsContext && this._context && this._context.state !== 'closed'
      ? this._context.close() : Promise.resolve();
    this._destroyPromise = Promise.resolve(closing).then(() => undefined);
    return this._destroyPromise;
  }

  _assertAlive() {
    if (this._destroyed) throw new Error('This WindChime instance has been destroyed.');
  }

  _assertRunning() {
    this._assertAlive();
    if (!this.isRunning || this._context?.state !== 'running') {
      throw new Error('Call and await start() from a user click or tap before playing chimes.');
    }
  }

  _emit(event, payload) {
    for (const listener of [...(this._listeners.get(event) || [])]) {
      try { listener(payload); }
      catch (error) { globalThis.console?.error('WindChime event listener failed:', error); }
    }
  }

  _setState(state, reason) {
    if (this._state === state) return;
    const previousState = this._state;
    this._state = state;
    this._emit('statechange', Object.freeze({ state, previousState, reason }));
  }

  _validateSampleRate(options) {
    if (options.scale.some((n) => options.baseFrequency * 2 ** (n / 12) *
        2 ** (options.detune / 1200) >= this._context.sampleRate * 0.45)) {
      throw new RangeError('A note is too high for this AudioContext sample rate. Lower baseFrequency or scale.');
    }
  }

  _initialize() {
    if (this._graph) return;
    if (!this._context) {
      const Context = globalThis.AudioContext || globalThis.webkitAudioContext;
      if (!Context) throw new Error('This browser does not support the Web Audio API.');
      this._context = new Context({ latencyHint: 'interactive' });
    }
    this._validateSampleRate(this._options);
    const ctx = this._context;
    const nodes = [];
    const make = (method) => { const node = ctx[method](); nodes.push(node); return node; };
    try {
      const input = make('createGain');
      const dry = make('createGain');
      const convolver = make('createConvolver');
      const wet = make('createGain');
      const tone = make('createBiquadFilter');
      const compressor = make('createDynamicsCompressor');
      const saturator = make('createWaveShaper');
      const master = make('createGain');
      const gate = make('createGain');
      tone.type = 'lowpass';
      tone.frequency.value = Math.min(6500, ctx.sampleRate * 0.4);
      tone.Q.value = 0.3;
      compressor.threshold.value = -14;
      compressor.knee.value = 12;
      compressor.ratio.value = 6;
      compressor.attack.value = 0.004;
      compressor.release.value = 0.3;
      const curve = new Float32Array(2048);
      for (let i = 0; i < curve.length; i++) curve[i] = Math.tanh(i * 2 / (curve.length - 1) - 1);
      saturator.curve = curve;
      saturator.oversample = '2x';
      convolver.buffer = this._impulse();
      input.connect(dry).connect(compressor);
      input.connect(convolver).connect(tone).connect(wet).connect(compressor);
      compressor.connect(saturator).connect(master).connect(gate).connect(this._destination || ctx.destination);
      gate.gain.value = 0;
      this._graph = { input, dry, wet, master, gate, nodes };
      this._applyMix(true);
      this._document = typeof document === 'undefined' ? null : document;
      this._document?.addEventListener('visibilitychange', this._onVisibility);
      ctx.addEventListener('statechange', this._onContextState);
    } catch (error) {
      for (const node of nodes) disconnect(node);
      this._graph = null;
      throw error;
    }
  }

  _impulse() {
    const ctx = this._context;
    const length = Math.floor(ctx.sampleRate * 2.6);
    const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
    const random = seededRandom(73421);
    for (let channel = 0; channel < 2; channel++) {
      const samples = buffer.getChannelData(channel);
      let filtered = 0;
      for (let i = 0; i < length; i++) {
        filtered = 0.65 * filtered + 0.35 * (2 * random() - 1);
        samples[i] = filtered * (1 - i / length) ** 3 * Math.min(1, i / (ctx.sampleRate * 0.008));
      }
    }
    return buffer;
  }

  _applyMix(immediate = false) {
    const { volume, muted, reverb } = this._options;
    const now = this._context.currentTime;
    const duration = immediate ? 0 : 0.04;
    smooth(this._graph.master.gain, muted ? 0 : volume, now, duration);
    smooth(this._graph.dry.gain, 1 - 0.35 * reverb, now, duration);
    smooth(this._graph.wet.gain, 0.65 * reverb, now, duration);
  }

  _syncPlayback() {
    if (this._destroyed || this._starting || !this._wantRunning || !this._graph) return;
    const hidden = this._options.pauseWhenHidden && this._document?.visibilityState === 'hidden';
    if (hidden || this._context.state !== 'running') {
      this._halt(0.08);
      this._setState('paused', hidden ? 'hidden' : 'audio-interrupted');
      return;
    }
    if (this._timer !== null) return;
    this._nextFrame = this._context.currentTime + 0.02;
    this._lastStrikeAt = this._context.currentTime;
    this._strikeBudget = 0; // Give audible feedback when starting in a nonzero breeze.
    this._windEventAt = 0;
    smooth(this._graph.gate.gain, 1, this._context.currentTime, 0.08);
    this._timer = globalThis.setInterval(() => this._tick(), STEP * 1000);
    this._setState('running', 'ready');
    // Listeners may call stop() or destroy() in response to the state event.
    if (!this._wantRunning || this._destroyed || this._state !== 'running') return;
    this._tick();
  }

  _halt(fadeOut) {
    if (this._timer !== null) globalThis.clearInterval(this._timer);
    this._timer = null;
    this._windLevel = 0;
    if (!this._graph) return;
    if (this._context.state === 'closed') {
      for (const voice of [...this._voices]) this._cleanupVoice(voice);
      return;
    }
    const now = this._context.currentTime;
    smooth(this._graph.gate.gain, 0, now, fadeOut);
    for (const voice of this._voices) {
      smooth(voice.output.gain, 0, now, fadeOut);
      for (const osc of voice.oscillators) {
        try { osc.stop(Math.min(voice.end, now + fadeOut + 0.01)); } catch { /* Already ended. */ }
      }
    }
  }

  _tick() {
    if (!this.isRunning || !this._wantRunning) return;
    if (this._context.state !== 'running') { this._syncPlayback(); return; }
    try {
      const now = this._context.currentTime;
      // Discard missed wall-clock time: returning to a busy tab must not cause a burst.
      if (this._nextFrame < now) this._nextFrame = now + 0.02;
      while (this._nextFrame < now + LOOKAHEAD && this.isRunning && this._wantRunning) {
        const time = this._nextFrame;
        this._nextFrame += STEP;
        this._advanceWind(time);
        if (!this.isRunning || !this._wantRunning) break;
        const rate = this._options.density * this._windLevel ** 1.7;
        const automaticPlaybackEnabled = this._options.density > 0 &&
          (this._options.wind > 0 || this._gust !== null);
        const maximumSilenceAt = this._lastStrikeAt + this._options.maximumSilence;
        const maximumSilenceReached = automaticPlaybackEnabled &&
          time + STEP >= maximumSilenceAt;
        this._strikeBudget -= rate * STEP;
        if ((rate > 0 && this._strikeBudget <= 0) || maximumSilenceReached) {
          this._strikeBudget = -Math.log(Math.max(1e-9, 1 - this._random()));
          const velocity = clamp((0.2 + this._windLevel * 0.75) * (0.65 + 0.35 * this._random()));
          const strikeTime = maximumSilenceReached ? Math.max(time, maximumSilenceAt) : time + this._random() * STEP;
          this._playStrike(strikeTime, null, velocity, undefined, 'wind');
        }
      }
    } catch (error) {
      this.stop({ fadeOut: 0.05 });
      this._emit('error', error);
    }
  }

  _advanceWind(time) {
    const o = this._options;
    if (time >= this._targetUntil) {
      this._targetUntil = time + 2 + this._random() * 7;
      this._targetWind = clamp(o.wind * (1 + (this._random() * 2 - 1) * 1.8 * o.variability));
      if (this._random() < 0.22 * o.variability) this._targetWind = 0;
    }
    this._baseWind += (this._targetWind - this._baseWind) * (1 - Math.exp(-STEP / 1.6));
    if (this._gust && time >= this._gust.start + this._gust.duration) this._gust = null;
    if (!this._gust && o.wind > 0 && this._random() < 1 - Math.exp(-o.gustiness * 0.16 * STEP)) {
      this._gust = {
        start: time, strength: o.wind * (0.35 + this._random() * 0.8),
        duration: 2 + this._random() * 5, source: 'natural',
      };
    }
    let gust = 0;
    if (this._gust) {
      const p = clamp((time - this._gust.start) / this._gust.duration);
      gust = this._gust.strength * Math.sin(Math.PI * p) ** 2;
    }
    this._windLevel = clamp(this._baseWind + gust);
    if (time >= this._windEventAt) {
      this._windEventAt = time + 0.25;
      this._emit('wind', Object.freeze({ level: this._windLevel, time }));
    }
  }

  _chooseNote() {
    const count = this._options.scale.length;
    if (count === 1) return 0;
    if (this._lastNote < 0 || this._lastNote >= count) return Math.floor(this._random() * count);
    const pick = Math.floor(this._random() * (count - 1));
    return pick >= this._lastNote ? pick + 1 : pick;
  }

  _playStrike(time, index, velocity, pan, source) {
    const o = this._options;
    if (o.muted || o.volume === 0 || velocity === 0 || this._voices.size >= o.maxVoices) return null;
    index = index === null ? this._chooseNote() : index;
    this._lastNote = index;
    const frequency = o.baseFrequency * 2 ** (o.scale[index] / 12);
    const actualPan = typeof this._context.createStereoPanner !== 'function' ? 0 : pan ?? clamp(
      ((o.scale.length === 1 ? 0 : index / (o.scale.length - 1) * 2 - 1) +
      (this._random() - 0.5) * 0.2) * o.stereoWidth, -1, 1);
    const ctx = this._context;
    const nodes = [];
    const oscillators = [];
    const voice = { nodes, oscillators, output: null, end: time, remaining: 0 };
    try {
      const output = ctx.createGain();
      nodes.push(output);
      voice.output = output;
      if (typeof ctx.createStereoPanner === 'function') {
        const panner = ctx.createStereoPanner();
        nodes.push(panner);
        panner.pan.value = actualPan;
        output.connect(panner).connect(this._graph.input);
      } else output.connect(this._graph.input);
      const shimmer = (this._random() * 2 - 1) * o.detune;
      const brightness = 0.25 + 0.75 * o.brightness;
      const modes = [[1, 1, 1], [1.004, 0.20, 0.9], [2.756, 0.38 * brightness, 0.61],
        [5.404, 0.19 * brightness, 0.37], [8.933, 0.09 * brightness, 0.24]]
        .filter(([ratio]) => frequency * ratio * 2 ** (Math.abs(shimmer) / 1200) < ctx.sampleRate * 0.45);
      const total = modes.reduce((sum, mode) => sum + mode[1], 0);
      for (const [ratio, weight, length] of modes) {
        const osc = ctx.createOscillator();
        const envelope = ctx.createGain();
        nodes.push(osc, envelope);
        oscillators.push(osc);
        osc.type = 'sine';
        osc.frequency.value = frequency * ratio;
        osc.detune.value = shimmer;
        const peak = Math.max(FLOOR, 0.45 * velocity * weight / total);
        const attack = 0.003 + this._random() * 0.005;
        const duration = o.decay * length * (0.88 + this._random() * 0.24);
        const end = time + duration + 0.03;
        voice.end = Math.max(voice.end, end);
        envelope.gain.setValueAtTime(0, time);
        envelope.gain.linearRampToValueAtTime(peak, time + attack);
        envelope.gain.exponentialRampToValueAtTime(FLOOR, time + duration);
        envelope.gain.linearRampToValueAtTime(0, end);
        osc.connect(envelope).connect(output);
        voice.remaining++;
        osc.onended = () => {
          if (--voice.remaining === 0) this._cleanupVoice(voice);
        };
        osc.start(time);
        osc.stop(end + 0.005);
      }
      if (voice.remaining === 0) { this._cleanupVoice(voice); return null; }
      this._voices.add(voice);
    } catch (error) {
      for (const osc of oscillators) { try { osc.stop(); } catch { /* Not started. */ } }
      this._cleanupVoice(voice);
      throw error;
    }
    const detail = Object.freeze({ index, frequency, velocity, pan: actualPan, time, source });
    this._lastStrikeAt = time;
    this._emit('strike', detail);
    return detail;
  }

  _cleanupVoice(voice) {
    for (const osc of voice.oscillators) osc.onended = null;
    for (const node of voice.nodes) disconnect(node);
    this._voices.delete(voice);
    if (this._voices.size === 0 && this._state === 'stopping') this._setState('stopped', 'drained');
  }
}

export { WindChime };
export default WindChime;
